import React, { useState } from 'react';
import { Box, Typography, Grid, TextField, Button, IconButton } from '@mui/material';
import { AddIcon, DeleteIcon } from 'icons/materialIcons';
import { useEmailSignatureContext } from 'contexts/EmailSignatureContext';
import TeamAddContactLinkModal from 'team/components/editMember/links/TeamAddContactLinkModal';
import { useAuth } from 'contexts/AuthContext';
import getIconForType from 'utils/icons/getIconForType';
import TeamIosDarkToggle from 'team/shared/components/button/TeamIosDarkToggle';
import TeamWhiteButtonWithIcon from 'team/shared/components/button/TeamWhiteButtonWithIcon';

// Reusable style for bordered toggle containers
const borderedToggleContainerStyle = {
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '8px 16px', // Reduced padding: less vertical (8px) and horizontal (16px)
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
};

const TeamCreateEmailSignatureForm = ({
    signatureName,
    onSignatureNameChange,
    defaultCardId,
    emailSignatureState,
    onCancel,
    onUpdate,
    isCreating = true,
    isSubmitting = false
}) => {
    const { authState } = useAuth();
    const [addLinkModalOpen, setAddLinkModalOpen] = useState(false);

    const {
        setEmailSignatureName,
        setEmailSignatureCompany,
        setEmailSignatureJobTitle,
        setEmailSignaturePhoneNumber,
        setEmailSignatureLocation,
        setEmailSignatureDisclaimer,
        setEmailSignatureIncludeBannerImage,
        setEmailSignatureIncludeQRCode,
        addEmailSignatureLinkType,
        removeEmailSignatureLinkType,
        setEmailSignatureShowName,
        setEmailSignatureShowPronouns,
        setEmailSignatureShowJobTitle,
        setEmailSignatureShowCompany,
        setEmailSignatureShowLocation,
        setEmailSignatureShowPhoneNumber,
        setEmailSignatureShowProfilePic,
        setEmailSignatureShowCompanyLogo,
    } = useEmailSignatureContext();

    const currentState = defaultCardId ? emailSignatureState[defaultCardId] : {};

    // Initialize toggle states from context
    const fieldToggles = {
        name: currentState?.emailSignatureShowName !== undefined ? currentState.emailSignatureShowName : true,
        pronouns: currentState?.emailSignatureShowPronouns !== undefined ? currentState.emailSignatureShowPronouns : false,
        jobTitle: currentState?.emailSignatureShowJobTitle !== undefined ? currentState.emailSignatureShowJobTitle : true,
        companyName: currentState?.emailSignatureShowCompany !== undefined ? currentState.emailSignatureShowCompany : true,
        location: currentState?.emailSignatureShowLocation !== undefined ? currentState.emailSignatureShowLocation : true,
        phoneNumber: currentState?.emailSignatureShowPhoneNumber !== undefined ? currentState.emailSignatureShowPhoneNumber : true,
        profilePic: currentState?.emailSignatureShowProfilePic !== undefined ? currentState.emailSignatureShowProfilePic : true,
        companyLogo: currentState?.emailSignatureShowCompanyLogo !== undefined ? currentState.emailSignatureShowCompanyLogo : true,
        qrCode: currentState?.emailSignatureIncludeQRCode !== undefined ? currentState.emailSignatureIncludeQRCode : true,
        banner: currentState?.emailSignatureIncludeBannerImage !== undefined ? currentState.emailSignatureIncludeBannerImage : false,
    };

    return (
        <>
            <Box sx={{
                width: '50%',
                borderRight: '1px solid #e0e0e0',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden',
            }}>
                {/* Scrollable Content */}
                <Box sx={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: 4,
                }}>
                    {/* Signature Name */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f", mb: 1 }}>
                            Signature Name *
                        </Typography>
                        <TextField
                            variant="outlined"
                            fullWidth
                            placeholder="Signature Name"
                            value={signatureName}
                            onChange={(e) => onSignatureNameChange(e.target.value)}
                            required
                            sx={{
                                backgroundColor: '#f7f7f7',
                                borderRadius: '8px',
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    '& fieldset': {
                                        border: 'none',
                                    },
                                },
                            }}
                        />
                    </Box>

                    {/* Information Section */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.9rem', fontWeight: 600, color: "#4f4f4f", mb: 2 }}>
                            Information
                        </Typography>

                        <Grid container spacing={2}>
                            {/* Name - Separate Bordered Container */}
                            <Grid item xs={6}>
                                <Box sx={borderedToggleContainerStyle}>
                                    <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#333', fontWeight: 500, flex: 1 }}>
                                        Name
                                    </Typography>
                                    <TeamIosDarkToggle
                                        checked={fieldToggles.name}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            if (defaultCardId) {
                                                setEmailSignatureShowName(defaultCardId, checked);
                                            }
                                        }}
                                    />
                                </Box>
                            </Grid>

                            {/* Pronouns - Separate Bordered Container */}
                            <Grid item xs={6}>
                                <Box sx={borderedToggleContainerStyle}>
                                    <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#333', fontWeight: 500, flex: 1 }}>
                                        Pronouns
                                    </Typography>
                                    <TeamIosDarkToggle
                                        checked={fieldToggles.pronouns}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            if (defaultCardId) {
                                                setEmailSignatureShowPronouns(defaultCardId, checked);
                                            }
                                        }}
                                    />
                                </Box>
                            </Grid>

                            {/* Job Title - Separate Bordered Container */}
                            <Grid item xs={6}>
                                <Box sx={borderedToggleContainerStyle}>
                                    <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#333', fontWeight: 500, flex: 1 }}>
                                        Job Title
                                    </Typography>
                                    <TeamIosDarkToggle
                                        checked={fieldToggles.jobTitle}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            if (defaultCardId) {
                                                setEmailSignatureShowJobTitle(defaultCardId, checked);
                                            }
                                        }}
                                    />
                                </Box>
                            </Grid>

                            {/* Company Name - Separate Bordered Container */}
                            <Grid item xs={6}>
                                <Box sx={borderedToggleContainerStyle}>
                                    <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#333', fontWeight: 500, flex: 1 }}>
                                        Company Name
                                    </Typography>
                                    <TeamIosDarkToggle
                                        checked={fieldToggles.companyName}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            if (defaultCardId) {
                                                setEmailSignatureShowCompany(defaultCardId, checked);
                                            }
                                        }}
                                    />
                                </Box>
                            </Grid>

                            {/* Location - Separate Bordered Container */}
                            <Grid item xs={6}>
                                <Box sx={borderedToggleContainerStyle}>
                                    <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#333', fontWeight: 500, flex: 1 }}>
                                        Location
                                    </Typography>
                                    <TeamIosDarkToggle
                                        checked={fieldToggles.location}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            if (defaultCardId) {
                                                setEmailSignatureShowLocation(defaultCardId, checked);
                                            }
                                        }}
                                    />
                                </Box>
                            </Grid>

                            {/* Phone Number - Separate Bordered Container */}
                            <Grid item xs={6}>
                                <Box sx={borderedToggleContainerStyle}>
                                    <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#333', fontWeight: 500, flex: 1 }}>
                                        Phone Number
                                    </Typography>
                                    <TeamIosDarkToggle
                                        checked={fieldToggles.phoneNumber}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            if (defaultCardId) {
                                                setEmailSignatureShowPhoneNumber(defaultCardId, checked);
                                            }
                                        }}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Images Section */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.9rem', fontWeight: 600, color: "#4f4f4f", mb: 2 }}>
                            Images
                        </Typography>

                        <Grid container spacing={2}>
                            {/* Profile Pic - Toggle with Description */}
                            <Grid item xs={12}>
                                <Box sx={borderedToggleContainerStyle}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#333', fontWeight: 500, mb: 0.5 }}>
                                            Profile Pic
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontSize: '0.7rem', color: "#757575" }}>
                                            Members profile photo will be displayed in their signature
                                        </Typography>
                                    </Box>
                                    <TeamIosDarkToggle
                                        checked={fieldToggles.profilePic}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            if (defaultCardId) {
                                                setEmailSignatureShowProfilePic(defaultCardId, checked);
                                            }
                                        }}
                                    />
                                </Box>
                            </Grid>

                            {/* Company Logo - Separate Bordered Container */}
                            <Grid item xs={6}>
                                <Box sx={borderedToggleContainerStyle}>
                                    <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#333', fontWeight: 500, flex: 1 }}>
                                        Company Logo
                                    </Typography>
                                    <TeamIosDarkToggle
                                        checked={fieldToggles.companyLogo}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            if (defaultCardId) {
                                                setEmailSignatureShowCompanyLogo(defaultCardId, checked);
                                            }
                                        }}
                                    />
                                </Box>
                            </Grid>

                            {/* QR Code - Separate Bordered Container */}
                            <Grid item xs={6}>
                                <Box sx={borderedToggleContainerStyle}>
                                    <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#333', fontWeight: 500, flex: 1 }}>
                                        QR Code
                                    </Typography>
                                    <TeamIosDarkToggle
                                        checked={fieldToggles.qrCode}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            if (defaultCardId) {
                                                setEmailSignatureIncludeQRCode(defaultCardId, checked);
                                            }
                                        }}
                                    />
                                </Box>
                            </Grid>

                            {/* Banner - Full Width */}
                            <Grid item xs={12}>
                                <Box sx={borderedToggleContainerStyle}>
                                    <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#333', fontWeight: 500, flex: 1 }}>
                                        Banner
                                    </Typography>
                                    <TeamIosDarkToggle
                                        checked={fieldToggles.banner}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            if (defaultCardId) {
                                                setEmailSignatureIncludeBannerImage(defaultCardId, checked);
                                            }
                                        }}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Links Section */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.9rem', fontWeight: 600, color: "#4f4f4f", mb: 1 }}>
                            Links
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.7rem', color: "#757575", mb: 2 }}>
                            Links will show up on member's email signatures if the assigned members have the respective links on their default card
                        </Typography>

                        {/* Display selected links above Add Link button */}
                        {currentState?.emailSignatureLinkTypes && currentState.emailSignatureLinkTypes.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                                {currentState.emailSignatureLinkTypes.map((linkType) => (
                                    <Box
                                        key={linkType}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            py: 1.5,
                                            px: 2,
                                            mb: 1,
                                            border: '1px solid #e0e0e0',
                                            borderRadius: '8px',
                                            backgroundColor: '#ffffff',
                                            '&:hover': {
                                                backgroundColor: '#f5f5f5',
                                            },
                                        }}
                                    >
                                        {/* Icon and Text */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    padding: '4px',
                                                    backgroundColor: '#f0f0f0',
                                                    borderRadius: '4px',
                                                    flexShrink: 0
                                                }}
                                            >
                                                {(() => {
                                                    const iconElement = getIconForType(linkType);
                                                    return React.cloneElement(iconElement, {
                                                        sx: {
                                                            ...(iconElement.props?.sx || {}),
                                                            fontSize: '24px',
                                                        },
                                                    });
                                                })()}
                                            </Box>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontSize: '0.85rem',
                                                    color: '#4f4f4f',
                                                    fontWeight: 500,
                                                }}
                                            >
                                                {linkType}
                                            </Typography>
                                        </Box>

                                        {/* Delete Button */}
                                        <IconButton
                                            size="small"
                                            onClick={() => defaultCardId && removeEmailSignatureLinkType(defaultCardId, linkType)}
                                            sx={{ color: '#666' }}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                ))}
                            </Box>
                        )}

                        {/* Add Link Button */}
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={() => {
                                    const linkTypes = currentState?.emailSignatureLinkTypes || [];
                                    if (linkTypes.length >= 6) {
                                        return; // Don't open modal if max reached
                                    }
                                    setAddLinkModalOpen(true);
                                }}
                                disabled={currentState?.emailSignatureLinkTypes?.length >= 6}
                                sx={{
                                    borderColor: '#2196f3',
                                    color: '#2196f3',
                                    textTransform: 'none',
                                    fontSize: '0.8rem',
                                    '&:hover': {
                                        borderColor: '#2196f3',
                                        backgroundColor: 'rgba(33, 150, 243, 0.04)',
                                    },
                                    '&:disabled': {
                                        borderColor: '#e0e0e0',
                                        color: '#999',
                                    },
                                }}
                            >
                                Add Link
                            </Button>
                        </Box>

                        {/* Max links error message */}
                        {currentState?.emailSignatureLinkTypes?.length >= 6 && (
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: '0.75rem',
                                    color: '#828282',
                                    textAlign: 'center',
                                    mt: 1,
                                }}
                            >
                                Max number of links reached. To add a new link, remove an existing one.
                            </Typography>
                        )}
                    </Box>

                    {/* Disclaimer Section */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.9rem', fontWeight: 600, color: "#4f4f4f", mb: 1 }}>
                            Disclaimer
                        </Typography>
                        <TextField
                            variant="outlined"
                            fullWidth
                            multiline
                            rows={3}
                            placeholder="Disclaimer"
                            value={currentState?.emailSignatureDisclaimer || ''}
                            onChange={(e) => defaultCardId && setEmailSignatureDisclaimer(defaultCardId, e.target.value)}
                            sx={{
                                backgroundColor: '#f7f7f7',
                                borderRadius: '8px',
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    '& fieldset': {
                                        border: 'none',
                                    },
                                },
                            }}
                        />
                    </Box>
                </Box>

                {/* Footer - Cancel and Update Buttons */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 2,
                        padding: 3,
                        borderTop: '1px solid #e0e0e0',
                        backgroundColor: '#ffffff',
                        flexShrink: 0,
                    }}
                >
                    <TeamWhiteButtonWithIcon
                        text="Cancel"
                        onClick={onCancel}
                        padding="8px 16px"
                        sx={{
                            minWidth: '120px',
                        }}
                    />
                    <Button
                        onClick={onUpdate}
                        variant="contained"
                        disabled={isSubmitting}
                        sx={{
                            minWidth: '120px',
                            textTransform: 'none',
                            backgroundColor: '#000000',
                            color: '#ffffff',
                            borderRadius: '20px',
                            padding: '8px 16px',
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            '&:hover': {
                                backgroundColor: '#000000',
                            },
                            '&:disabled': {
                                backgroundColor: '#cccccc',
                                color: '#ffffff'
                            }
                        }}
                    >
                        {isSubmitting ? (isCreating ? 'Creating...' : 'Updating...') : (isCreating ? 'Create' : 'Update')}
                    </Button>
                </Box>
            </Box>

            {/* Add Link Modal */}
            <TeamAddContactLinkModal
                open={addLinkModalOpen}
                handleClose={() => setAddLinkModalOpen(false)}
                isTemporaryCard={false}
                userId={authState?.user?.userId}
                cardId={defaultCardId}
                existingContactLinks={[]}
                isInSignupFlow={false}
                isEmailSignatureMode={true}
                emailSignatureLinkTypes={currentState?.emailSignatureLinkTypes || []}
                onLinkTypeSelect={(linkType) => {
                    if (defaultCardId) {
                        const linkTypes = emailSignatureState[defaultCardId]?.emailSignatureLinkTypes || [];

                        // Check if link type already exists
                        if (linkTypes.includes(linkType)) {
                            // Link type already added, don't add again
                            setAddLinkModalOpen(false);
                            return;
                        }

                        // Check if max links reached
                        if (linkTypes.length >= 6) {
                            setAddLinkModalOpen(false);
                            return;
                        }

                        addEmailSignatureLinkType(defaultCardId, linkType);
                        setAddLinkModalOpen(false);
                    }
                }}
            />
        </>
    );
};

export default TeamCreateEmailSignatureForm;

